import React from 'react';
import { Outlet, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { HomlyLogo } from '../brand/HomlyLogo';

// Animated gradient orb
function GradientOrb({ className }) {
    return (
        <motion.div
            className={`absolute rounded-full blur-[100px] ${className}`}
            animate={{
                scale: [1, 1.15, 1],
                opacity: [0.08, 0.14, 0.08],
            }}
            transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut"
            }}
        />
    );
}

function AuthLayout() {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (isAuthenticated) {
        if (user?.role === 'super_admin') return <Navigate to="/homly-internal" replace />;
        if (user?.role === 'admin') return <Navigate to="/admin" replace />;
        if (user?.role === 'agent') return <Navigate to="/agent" replace />;
        if (user?.role === 'seller') return <Navigate to="/seller" replace />;
        if (user?.role === 'professional') return <Navigate to="/pro/dashboard" replace />;
    }

    return (
        <div className="min-h-screen bg-background flex relative overflow-hidden">
            {/* Background effects */}
            <GradientOrb className="w-[500px] h-[500px] bg-[#681793] -top-40 -left-40" />
            <GradientOrb className="w-[400px] h-[400px] bg-[#FF914D] bottom-0 right-0" />
            
            {/* Grid pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(104,23,147,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(104,23,147,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

            {/* Left side - Form */}
            <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8 relative z-10">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <Link to="/" className="block text-center mb-8">
                        <HomlyLogo size="lg" />
                        <p className="text-sm text-muted-foreground mt-1">La plateforme immobilière intelligente</p>
                    </Link>
                    <motion.div 
                        className="bg-card backdrop-blur-xl rounded-2xl border border-border p-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Outlet />
                    </motion.div>
                    <div className="mt-6 text-center text-sm text-muted-foreground">
                        <Link to="/cgu" className="hover:text-white transition-colors">CGU</Link>
                        <span className="mx-2">·</span>
                        <Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link>
                        <span className="mx-2">·</span>
                        <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
                    </div>
                </div>
            </div>

            {/* Right side - Visual (desktop only) */}
            <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
                <div className="relative z-10 max-w-md">
                    {/* Floating card mockup */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <div className="bg-muted backdrop-blur-xl rounded-3xl p-6 border border-border shadow-2xl">
                            <div className="flex items-center gap-3 pb-4 border-b border-border mb-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-black text-lg">H</div>
                                <div>
                                    <p className="font-bold text-white">Dashboard</p>
                                    <p className="text-xs text-muted-foreground">Votre espace de gestion</p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { label: 'Mandats actifs', value: '24', color: 'from-[#681793] to-[#a855f7]' },
                                    { label: 'Signatures ce mois', value: '18', color: 'from-[#FF914D] to-[#f59e0b]' },
                                    { label: 'Taux de conversion', value: '94%', color: 'from-[#22c55e] to-[#4ade80]' }
                                ].map((stat, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-card border border-white/5">
                                        <span className="text-sm text-muted-foreground">{stat.label}</span>
                                        <span className={`font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>{stat.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Floating badge */}
                    <motion.div
                        className="absolute -top-3 -right-3 bg-gradient-to-r from-[#681793] to-[#FF914D] rounded-full px-4 py-2 shadow-lg shadow-[#681793]/40"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1, y: [-5, 5, -5] }}
                        transition={{ 
                            opacity: { duration: 0.5, delay: 0.5 },
                            scale: { duration: 0.5, delay: 0.5 },
                            y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                        }}
                    >
                        <p className="text-white text-xs font-bold flex items-center gap-1.5 whitespace-nowrap">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></svg>
                            Mandat signé
                        </p>
                    </motion.div>

                    {/* Quote */}
                    <motion.div 
                        className="mt-8 text-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8 }}
                    >
                        <h2 className="text-2xl font-bold text-white mb-3">
                            Gérez vos mandats
                            <span className="block bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                en toute simplicité
                            </span>
                        </h2>
                        <p className="text-muted-foreground">
                            Cockpit unifié, suivi vendeur et rémunérations réunis dans une seule plateforme.
                        </p>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

export default AuthLayout;
